import {
  API_BASE_URL,
  API_ENDPOINTS,
  ENABLE_SYSTEM_DESIGN_INTERVIEW,
  TF_ENABLE_COMBO_PLAN,
} from '@/lib/config'
import { createCoalescedFetcher } from '@/lib/requestCoalesce'

export interface ProductCreditBreakdownItem {
  credit_type_id: string
  code: string
  name: string
  credits: number
}

export interface ProductPack {
  product_id: string
  product_code: string
  product_name: string
  /** Canonical INR checkout / PayU amount — never use for landing display. */
  price: number
  /** Localized landing display amount (may differ from ``price`` for USD). */
  display_price: number
  /** Sum of (credits × unit price) for combo packs; null for individual products. */
  original_price: number | null
  /** ISO 4217 currency for ``display_price`` (uniform across the packs response). */
  currency: string
  payment_plan_type: string
  validity_days: number | null
  is_active: boolean
  credit_breakdown: ProductCreditBreakdownItem[]
}

export type InterviewPracticePlanType = 'skill_assessment' | 'jd_prep' | 'system_design'

export const INTERVIEW_PRACTICE_PLAN_TYPES: InterviewPracticePlanType[] = [
  'skill_assessment',
  'jd_prep',
  'system_design',
]

export function isInterviewPracticeProduct(pack: ProductPack): boolean {
  return INTERVIEW_PRACTICE_PLAN_TYPES.includes(
    pack.payment_plan_type as InterviewPracticePlanType,
  )
}

export function isComboPack(pack: ProductPack): boolean {
  return (
    pack.payment_plan_type === 'interview_combo' ||
    pack.credit_breakdown.filter((c) => c.credits > 0).length > 1
  )
}

/**
 * Campaign-only combo. The backend product row must use this code.
 * It stays off the public pricing grids so COMBO_5_PACK keeps its own price.
 */
export const DUSSEHRA_OFFER_PRODUCT_CODE = 'DUSSEHRA_COMBO_2026'

export function isDussehraOfferPack(pack: ProductPack): boolean {
  return pack.product_code === DUSSEHRA_OFFER_PRODUCT_CODE
}

export function filterComboPacks(packs: ProductPack[]): ProductPack[] {
  let filtered = packs.filter((p) => !isInterviewPracticeProduct(p))
  if (!TF_ENABLE_COMBO_PLAN) {
    filtered = filtered.filter((p) => !isComboPack(p))
  }
  if (!ENABLE_SYSTEM_DESIGN_INTERVIEW) {
    filtered = filtered.filter(
      (p) => !p.credit_breakdown.some((c) => c.code === 'system_design' && c.credits > 0),
    )
  }
  return filtered
}

/** Landing display amount for an interview practice plan type. */
export function getInterviewPracticeDisplayPrice(
  packs: ProductPack[],
  planType: InterviewPracticePlanType,
): number | undefined {
  const pack = packs.find((p) => p.payment_plan_type === planType)
  if (!pack) return undefined
  return pack.display_price ?? pack.price
}

/** Display currency for interview practice cards (uniform across packs response). */
export function getInterviewPracticeCurrency(packs: ProductPack[]): string {
  const practice = packs.find((p) => isInterviewPracticeProduct(p))
  return practice?.currency || packs[0]?.currency || 'INR'
}

/** @deprecated Prefer getInterviewPracticeDisplayPrice for landing UI. */
export function getInterviewPracticePrice(
  packs: ProductPack[],
  planType: InterviewPracticePlanType,
): number | undefined {
  return getInterviewPracticeDisplayPrice(packs, planType)
}

// Pricing packs are country-dependent — coalesce concurrent callers on one page load
// (LandingPricing + LandingPricingSection) with a short per-tab TTL.
const _packsFetcher = createCoalescedFetcher<ProductPack[]>(60_000)

function normalizePack(raw: ProductPack): ProductPack {
  return {
    ...raw,
    display_price:
      typeof raw.display_price === 'number' ? raw.display_price : raw.price,
    original_price: typeof raw.original_price === 'number' ? raw.original_price : null,
    currency: raw.currency || 'INR',
  }
}

export function fetchProductPacks(): Promise<ProductPack[]> {
  return _packsFetcher('packs', async () => {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_PACKS}`, {
      cache: 'no-store',
    })
    if (!res.ok) {
      throw new Error(`Failed to fetch product packs (${res.status})`)
    }
    const data = (await res.json()) as ProductPack[]
    return data.map(normalizePack)
  })
}
